import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertCircle, ArrowUpRight, Building2, CalendarDays, ChevronRight, Clock3, Sparkles } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { formatHomeDate, getHomeGreeting, groupHomeItems } from '@/lib/home';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
};

const formatDate = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(value));
};

const SECTION_META = {
  Vandaag: { icon: CalendarDays, iconClass: 'bg-teal-50 text-teal-700' },
  'Aandacht nodig': { icon: AlertCircle, iconClass: 'bg-amber-50 text-amber-700' },
  'Mijn planning': { icon: CalendarDays, iconClass: 'bg-slate-100 text-slate-600' },
  'Organisatie vraagt aandacht': { icon: Building2, iconClass: 'bg-orange-50 text-orange-700' },
};

const PriorityBadge = ({ priority }) => {
  if (priority === 'urgent') return <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-bold text-rose-700">Urgent</span>;
  if (priority === 'important') return <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700">Hoog</span>;
  return null;
};

const HomeSection = ({ title, items, dateMode = 'time', tone = 'default' }) => {
  if (!items.length) return null;
  const meta = SECTION_META[title] || SECTION_META['Mijn planning'];
  const Icon = meta.icon;
  return (
    <section aria-labelledby={`home-${title}`} data-testid={`home-section-${title.toLowerCase().replaceAll(' ', '-')}`}>
      <div className="mb-2.5 flex items-center justify-between px-1">
        <div className="flex items-center gap-2.5">
          <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${meta.iconClass}`}><Icon className="h-4 w-4" /></span>
          <h2 id={`home-${title}`} className="text-sm font-bold tracking-tight text-slate-800">{title}</h2>
        </div>
        <span className="text-xs font-medium text-slate-400">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
      </div>
      <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${tone === 'attention' ? 'border-amber-200/80' : 'border-slate-200/80'}`}>
        {items.map((item) => (
          <a
            key={item.id}
            href={item.sourceRoute}
            className="group flex min-h-[72px] items-center gap-3 border-b border-slate-100/90 px-4 py-3.5 transition-colors last:border-b-0 hover:bg-slate-50 sm:gap-4 sm:px-5"
          >
            <div className="w-[58px] shrink-0 text-xs font-semibold leading-4 text-slate-500 sm:w-[72px] sm:text-sm">
              {dateMode === 'date' ? formatDate(item.startsAt || item.dueAt) : item.startsAt ? formatTime(item.startsAt) : <span className="text-slate-400">Actie</span>}
            </div>
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 font-semibold leading-5 text-slate-900">{item.title}</p>
              <p className="line-clamp-2 text-sm text-slate-500">
                {item.subtitle || (item.dueAt ? `Vóór ${formatDate(item.dueAt)}` : '')}
              </p>
            </div>
            <PriorityBadge priority={item.priority} />
            <ChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500" />
          </a>
        ))}
      </div>
    </section>
  );
};

const ZoeaDevelopmentSection = ({ items }) => {
  if (!items.length) return null;
  const summary = items.find((item) => item.id === 'zoea:usecases:summary');
  const usecases = items.filter((item) => item.id !== 'zoea:usecases:summary').slice(0, 3);
  const summaryParts = summary?.title?.split(' · ') || [];

  return (
    <section aria-labelledby="home-zoea-ontwikkeling" data-testid="home-section-zoea-ontwikkeling">
      <div className="mb-2.5 flex items-center gap-2.5 px-1">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><Sparkles className="h-4 w-4" /></span>
        <div>
          <h2 id="home-zoea-ontwikkeling" className="text-sm font-bold tracking-tight text-slate-800">Zoea ontwikkeling</h2>
          <p className="text-[11px] font-medium text-slate-400">Productaandacht</p>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm">
        {summary && (
          <div className="border-b border-violet-100 bg-gradient-to-br from-violet-50/80 to-white px-3 py-3 sm:px-5 sm:py-4">
            <div className="flex gap-1.5 overflow-x-auto pb-0.5 sm:flex-wrap sm:gap-2">
              {summaryParts.map((part) => <span key={part} className="shrink-0 rounded-lg bg-white/90 px-2 py-1.5 text-[11px] font-semibold text-violet-800 shadow-sm ring-1 ring-violet-100 sm:px-2.5 sm:text-xs">{part}</span>)}
            </div>
          </div>
        )}
        {usecases.map((item) => (
          <a key={item.id} href={item.sourceRoute} className="group flex min-h-[60px] items-center gap-2.5 border-b border-slate-100 px-3 py-2.5 transition-colors last:border-b-0 hover:bg-violet-50/40 sm:min-h-[68px] sm:gap-3 sm:px-5 sm:py-3">
            <span className={`h-2 w-2 shrink-0 rounded-full ${item.priority === 'important' ? 'bg-amber-500' : 'bg-violet-300'}`} />
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900">{item.title}</p>
              <p className="line-clamp-1 text-xs text-slate-500">{item.subtitle}</p>
            </div>
            {item.priority === 'important' && <span className="hidden shrink-0 rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700 sm:inline-flex">Hoog</span>}
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-violet-500" />
          </a>
        ))}
        {summary && (
          <a href={summary.sourceRoute} className="group flex items-center justify-between border-t border-violet-100 bg-violet-50/30 px-3 py-3 text-sm font-semibold text-violet-800 transition-colors hover:bg-violet-50 sm:px-5 sm:py-3.5">
            <span>{summary.subtitle || 'Bekijk alle usecases'}</span>
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        )}
      </div>
    </section>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [partial, setPartial] = useState(false);
  const items = useMemo(() => groupHomeItems(home?.items || []), [home]);
  const now = new Date();
  const firstName = user?.name?.trim()?.split(/\s+/)[0] || 'daar';

  useEffect(() => {
    let active = true;
    axios.get(`${API}/home`)
      .then((response) => {
        if (!active) return;
        setHome(response.data);
        setFailed(false);
        setPartial(response.data.status === 'partial' || response.data.status === 'unavailable' || response.data.complete === false);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl space-y-6" data-testid="dashboard-page">
        <header className="rounded-2xl border border-slate-200/70 bg-white/70 px-5 py-5 shadow-sm sm:px-7 sm:py-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">Zoea Home</p>
          <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {getHomeGreeting(now.getHours())}, {firstName}
          </h1>
          <p className="mt-1.5 flex items-center gap-2 text-sm text-slate-500">
            <Clock3 className="h-3.5 w-3.5 text-slate-400" />
            <span>{formatHomeDate(now)} <span className="px-1 text-slate-300">·</span> {home?.organization?.name || 'Zoea'}</span>
          </p>
        </header>

        {loading && <p className="text-sm text-slate-500" role="status">Je overzicht wordt geladen…</p>}
        {(failed || partial) && !loading && (
          <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" role="status">
            Een deel van je overzicht kon niet worden geladen.
          </p>
        )}
        {!loading && !failed && (
          <>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(300px,0.85fr)]">
              <div className="space-y-6">
                <HomeSection title="Vandaag" items={items.today} />
                <HomeSection title="Aandacht nodig" items={items.attention} dateMode="date" tone="attention" />
                <HomeSection title="Mijn planning" items={items.planning} dateMode="date" />
              </div>
              <div className="space-y-6">
                <HomeSection title="Organisatie vraagt aandacht" items={items.organization_attention} dateMode="date" tone="attention" />
                <ZoeaDevelopmentSection items={items.zoea_development} />
              </div>
            </div>
            {!items.today.length && !items.attention.length && !items.planning.length && !items.organization_attention.length && !items.zoea_development.length && (
              <p className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-600" data-testid="home-empty-state">
                Alles is bijgewerkt voor vandaag.
              </p>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
